import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber, {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import PartyAvatarList from 'ui-library/PartyAvatarList';
import {listToTruncatedReadable} from 'util/NameUtil';
import omitKey from 'util/omitKey';

import {CollaborationManagementStateQuery} from './__generated/CollaborationManagementStateQuery';
import CollaborationManagementModal from './CollaborationManagementModal';
import * as PoliceableActions from './PoliceableActions';
import useCollaborationManagementState, {
  CollaborationManagementState,
  SuccessfulCollaborationManagementState,
  CollaborationManagementActions,
  Collaboration,
} from './useCollaborationManagementState';

type Party = ExtractByTypename<
  CollaborationManagementStateQuery['policeable'],
  'ConnectionSchemaNode'
>['inheritedCollaborations'][number]['party'];

type CollaborationManagementSectionProps = Omit<SuccessfulCollaborationManagementState, 'queryResultType'> &
  CollaborationManagementActions & {
    onRequestSubmit?: (collaborations: Array<Collaboration>) => Promise<void>;
    policeableName: string;
    onRequestClose?: () => void;
    closeParentModal?: () => void;
  };

export const NUM_COLLABORATORS_TO_SHOW = 4;

type MinimalParty = Pick<Party, 'id' | 'name' | 'avatarUrl'>;

function uniquePartiesFromCollaborations(collaborations: Array<{party: MinimalParty}>): Array<MinimalParty> {
  return collaborations
    .map((c) => c.party)
    .filter(
      (party: MinimalParty, i: number, a: Array<MinimalParty>) =>
        a.findIndex((party2: MinimalParty) => party2.id === party.id) === i
    );
}

function CollaborationManagementSection(props: CollaborationManagementSectionProps): JSX.Element {
  const collaborators = uniquePartiesFromCollaborations([
    ...props.collaborations,
    ...props.ancestorCollaborations,
  ]);
  const collaboratorNames = collaborators.map((c) => c.name);
  // We do not truncate the collaborators if the length is the exact amount we want to show to prevent
  // displaying "and 1 more".
  const numCollaboratorsForTruncation =
    collaborators.length === NUM_COLLABORATORS_TO_SHOW
      ? NUM_COLLABORATORS_TO_SHOW
      : NUM_COLLABORATORS_TO_SHOW - 1;

  return (
    <>
      <Label>Collaborators</Label>
      <Row>
        <Col sm={3}>
          <PartyAvatarList
            parties={collaborators}
            maxAvatars={NUM_COLLABORATORS_TO_SHOW}
            size={AvatarSize.SMALL}
          />
        </Col>
        <Col sm={6}>{`Shared with ${listToTruncatedReadable(
          collaboratorNames,
          numCollaboratorsForTruncation
        )}`}</Col>
        <Col sm={3}>
          <Button
            buttonStyle={ButtonStyle.LINK}
            text="Manage Access"
            onClick={async () => {
              await legacyRunModal(CollaborationManagementModal, {
                ...props,
                submitButtonText: 'Done',
                onRequestSubmit: props.onRequestSubmit,
                policeableName: props.policeableName,
              });
            }}
            dataTestAttrs={{component: 'CollaborationManagementSection', element: 'manageAccessButton'}}
          />
        </Col>
      </Row>
    </>
  );
}

type CollaborationManagementSectionOrLoadingProps = CollaborationManagementState &
  CollaborationManagementActions & {
    renderAuditorColumn?: boolean;
    onRequestSubmit?: (collaborations: Array<Collaboration>) => Promise<void>;
    policeableName: string;
    onRequestClose?: () => void;
    closeParentModal?: () => void;
  };

export default function CollaborationManagementSectionOrLoading(
  props: CollaborationManagementSectionOrLoadingProps
): JSX.Element {
  if (props.queryResultType === QueryResultType.ERROR) {
    notifications.error('Could not load collaborators; try reloading the page.');
    return <></>;
  } else if (props.queryResultType !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  } else {
    return <CollaborationManagementSection {...omitKey(props, 'queryResultType')} />;
  }
}

export function CompactCollaborationManagementSection({policeableId}: {policeableId: string}): JSX.Element {
  const [state, _] = useCollaborationManagementState(policeableId);
  if (state.queryResultType === QueryResultType.ERROR) {
    notifications.error('Could not load collaborators; try reloading the page.');
    return <></>;
  } else if (state.queryResultType !== QueryResultType.SUCCESS) {
    return <HorizontalThrobber />;
  }
  const uniqueParties = uniquePartiesFromCollaborations([
    ...state.collaborations,
    ...state.ancestorCollaborations,
  ]);
  const partiesToShow = uniqueParties.slice(0, NUM_COLLABORATORS_TO_SHOW);
  return (
    <>
      <div>
        <PartyAvatarList
          className={css(styles.compactAvatarList)}
          avatarClassName={css(styles.compactAvatar)}
          parties={partiesToShow}
          maxAvatars={NUM_COLLABORATORS_TO_SHOW}
          size={AvatarSize.XXSMALL}
        />
        {uniqueParties.length > NUM_COLLABORATORS_TO_SHOW && (
          <span className="u-tiny-padding-left">+ more</span>
        )}
      </div>
      <div className="u-tiny-padding-top">
        <Button
          buttonStyle={ButtonStyle.LINK}
          text="Manage Access"
          onClick={() => {
            PoliceableActions.openSavingCollaborationManagementModal(policeableId);
          }}
        />
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  compactAvatarList: {
    marginRight: 8,
  },
  compactAvatar: {
    height: 20,
    width: 20,
    marginRight: -8,
    border: '2px solid white',
  },
});
